/**
 * Polls the background catalog-refresh job status.
 * Cheap when idle: the poll re-arms only while a run is live, plus a short grace window
 * afterwards so a run that vanished without an outcome can still be recognised.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';
import { useSmswiserCatalogApiEnabled } from '@/hooks/smswiser/catalog/useSmswiserCatalogApiEnabled';
import { catalogRefreshJobKeys } from './catalogRefreshJobKeys';
import type { CatalogRefreshJobState } from './types';

export const POLL_MS = 3000;
/**
 * Polls to keep taking after a run disappears. A run whose replica lost the lease publishes
 * no outcome, so "not running" can mean "finished", "blipped" or "died"; without these
 * extra polls the poller would stop on the first ambiguous answer and the watcher upstream
 * could never tell the three apart.
 */
export const GRACE_POLLS_AFTER_RUN = 4;

export function useCatalogRefreshJob(enabled = true) {
  const queryEnabled = useSmswiserCatalogApiEnabled(enabled);
  const grace = useRef(0);
  return useQuery({
    queryKey: catalogRefreshJobKeys.status(),
    queryFn: async (): Promise<CatalogRefreshJobState> => {
      const res = await api.get<{ data: CatalogRefreshJobState }>(
        '/smswiser/catalog/refresh/status',
      );
      return res.data.data;
    },
    refetchInterval: (query) => {
      if (query.state.data?.isRunning) {
        grace.current = GRACE_POLLS_AFTER_RUN;
        return POLL_MS;
      }
      if (grace.current <= 0) return false;
      grace.current -= 1;
      return POLL_MS;
    },
    // The global default is 60s: on mount that would replay a finished run as still
    // running (and never re-arm the poller). Job state is only ever read live.
    staleTime: 0,
    retry: false,
    enabled: queryEnabled,
  });
}
